import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';

import UsersClient from 'src/clients/UsersClient';
import {
  INVALID_CODE,
  REDIRECT_PATH_AFTER_GOOGLE_LOGIN,
  SESSION_EXPIRY_ERR_MSG,
} from 'src/constants/authConsts';
import {
  getAuthRequestInfo,
  getIdentityProviderForPath,
  GetSignInMetadata,
  GetSignInMetadataInput,
} from 'src/utils/AuthUtils';
import { ensureApiError } from 'src/utils/Errors';
import { recordProxySession } from 'src/utils/proxySession';
import { removeSpaceToLowerCase } from 'src/utils/StringUtils';

export type InternalUserMfaChallenge = {
  type: 'SOFTWARE_TOKEN_MFA';
  email: string;
  user: CognitoUser;
};

export type PasswordSignInResult =
  | { status: 'complete'; user: CognitoUser }
  | { status: 'mfa'; challenge: InternalUserMfaChallenge }
  | { status: 'idp_redirect' }
  | { status: 'error'; message: string };

/** Domain-gated SSO (e.g. capitalone.com) takes priority over magic-link login. */
export const tryIdentityProviderSignIn = async ({
  email,
  pathname,
}: {
  email: string;
  pathname: string;
}): Promise<boolean> => {
  const identityProvider = getIdentityProviderForPath({ email, pathname });
  if (!identityProvider) {
    return false;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const next = urlParams.get('next');
  if (next) {
    window.sessionStorage.setItem(REDIRECT_PATH_AFTER_GOOGLE_LOGIN, next);
  }
  await Auth.federatedSignIn({ customProvider: identityProvider });
  return true;
};

export const completeInternalUserSignIn = async (
  cognitoUser: CognitoUser,
  userEmail: string,
  userPoolWebClientId: string,
  onAuthComplete: () => void,
): Promise<void> => {
  const email = removeSpaceToLowerCase(userEmail);
  await UsersClient.setAppUserCookie(
    cognitoUser,
    'workspace_login',
    'workspaces',
    email,
  );
  window.localStorage.setItem(
    `CognitoIdentityServiceProvider.${userPoolWebClientId}.LastAuthUser`,
    cognitoUser.getUsername(),
  );

  recordProxySession(
    cognitoUser.getUsername(),
    window.location.pathname.includes('/admin'),
  );

  onAuthComplete();
};

export const startPasswordSignIn = async ({
  email,
  password,
  pathname,
  query,
}: {
  email: string;
  password: string;
  pathname: string;
  query: GetSignInMetadataInput;
}): Promise<PasswordSignInResult> => {
  if (await tryIdentityProviderSignIn({ email, pathname })) {
    return { status: 'idp_redirect' };
  }

  try {
    const requestInfo = await getAuthRequestInfo();
    const cognitoUser = await Auth.signIn(
      email,
      password,
      GetSignInMetadata(query, requestInfo),
    );

    if (cognitoUser.challengeName === 'SOFTWARE_TOKEN_MFA') {
      return {
        status: 'mfa',
        challenge: {
          type: 'SOFTWARE_TOKEN_MFA',
          email,
          user: cognitoUser,
        },
      };
    }

    return { status: 'complete', user: cognitoUser };
  } catch (err) {
    const e = ensureApiError(err);
    let { message } = e;
    if (message.includes('UserMigration failed')) {
      message = 'Invalid email or password';
    }
    return { status: 'error', message };
  }
};

export const completeMfaSignIn = async ({
  user,
  code,
  query,
}: {
  user: CognitoUser;
  code: string;
  query: GetSignInMetadataInput;
}): Promise<
  | { status: 'complete'; user: CognitoUser }
  | { status: 'error'; message: string }
> => {
  try {
    const requestInfo = await getAuthRequestInfo();
    const clientMetadata = GetSignInMetadata(query, requestInfo);
    const cognitoUser = await Auth.confirmSignIn(
      user,
      code,
      'SOFTWARE_TOKEN_MFA',
      clientMetadata,
    );
    return { status: 'complete', user: cognitoUser };
  } catch (err) {
    const e = ensureApiError(err);
    let { message } = e;
    if (message.includes('session is expired')) {
      message = SESSION_EXPIRY_ERR_MSG;
    } else if (e.code === 'CodeMismatchException') {
      message = INVALID_CODE;
    }
    return { status: 'error', message };
  }
};
